import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useParams } from 'react-router'
import { MRT_Updater, MRT_VisibilityState } from 'material-react-table'
import { ProjectGridField } from '@/types/fields'
import { WorkflowState } from '@/types/workflows'
import { createDataLineageColumn } from '@/utils/field'
import { useViewsContext } from './ViewsProvider'

type ColumnVisibilityContextValue = {
  columnVisibility: MRT_VisibilityState
  handleColumnVisibilityChange: (
    columnVisibilityUpdaterOrValue: MRT_Updater<MRT_VisibilityState>,
  ) => void // we use this function when we need to enable the user to save/update view
  resetColumnVisibility: () => void
  setColumnVisibility: Dispatch<SetStateAction<MRT_VisibilityState>>
}

const ColumnVisibilityContext = createContext<ColumnVisibilityContextValue>(
  {} as ColumnVisibilityContextValue,
)

export const useColumnVisibilityContext = () =>
  useContext(ColumnVisibilityContext)

type ColumnVisibilityProviderProps = {
  baseGridNonGridFields: ProjectGridField[]
  children: ReactNode
  selectedWorkflowState?: WorkflowState
}

export default function ColumnVisibilityProvider({
  baseGridNonGridFields,
  children,
  selectedWorkflowState,
}: ColumnVisibilityProviderProps) {
  const { view } = useParams()
  const baseFieldsVisibility = useMemo(() => {
    const baseFields = baseGridNonGridFields.reduce((acc, { id }) => {
      const columns = createDataLineageColumn({ fieldId: id })
      return { ...acc, ...columns }
    }, {})
    const onDocumentsView = view === 'documents'
    return {
      ...baseFields,
      fileColId: true,
      workflowStateColId: !selectedWorkflowState,
      organizationColId: true,
      submitterColId: onDocumentsView,
      pageCountColId: onDocumentsView,
      uploadColId: onDocumentsView,
      enteredAtColId: onDocumentsView,
      tagColId: onDocumentsView,
    }
  }, [baseGridNonGridFields, selectedWorkflowState, view])

  const { selectedView, handleChange } = useViewsContext()

  const visibility = useMemo(
    () =>
      Object.keys(selectedView.state.column_visibility || {}).length
        ? selectedView.state.column_visibility
        : baseFieldsVisibility,
    [baseFieldsVisibility, selectedView.state.column_visibility],
  )
  const [columnVisibility, setColumnVisibility] =
    useState<MRT_VisibilityState>(visibility)

  const handleColumnVisibilityChange = (
    columnVisibilityUpdaterOrValue: MRT_Updater<MRT_VisibilityState>,
  ) => {
    handleChange()
    if (columnVisibilityUpdaterOrValue instanceof Function) {
      setColumnVisibility((prev = {}) => columnVisibilityUpdaterOrValue(prev))
    } else {
      setColumnVisibility(columnVisibilityUpdaterOrValue)
    }
  }

  const resetColumnVisibility = () => {
    setColumnVisibility(visibility)
  }

  useEffect(() => {
    setColumnVisibility((prev) => ({
      ...prev,
      workflowStateColId: !selectedWorkflowState,
    }))
  }, [selectedWorkflowState])

  return (
    <ColumnVisibilityContext.Provider
      value={{
        columnVisibility,
        handleColumnVisibilityChange,
        resetColumnVisibility,
        setColumnVisibility,
      }}
    >
      {children}
    </ColumnVisibilityContext.Provider>
  )
}
